import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allWorks } from '@/mocks/pagesData';
import { categories } from '@/mocks/homeData';

export default function CategoryPage() {
  const { slug = 'all' } = useParams();
  const [activeCategory, setActiveCategory] = useState(slug);

  useEffect(() => {
    setActiveCategory(slug);
    window.scrollTo(0, 0);
  }, [slug]);

  const filteredWorks = useMemo(() => {
    if (activeCategory === 'all') return allWorks;
    return allWorks.filter((w) => w.categorySlug === activeCategory);
  }, [activeCategory]);

  const currentCategoryName = categories.find((c) => c.id === activeCategory)?.name || '全部作品';

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-3" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">{currentCategoryName}</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">{currentCategoryName}</h1>
            <p className="text-sm text-foreground-500 mt-1">共 {filteredWorks.length} 部作品</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center gap-2 flex-wrap mb-8 pb-2 overflow-x-auto">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300 hover:text-primary-500'
                }`}
              >
                <i className={`${cat.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredWorks.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-file-list-3-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无作品</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredWorks.map((work) => (
                <Link
                  key={work.id}
                  to={`/article/${work.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={work.image}
                      alt={work.title}
                      title={`${work.title} - 麻豆传媒${work.category}`}
                      className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-background-950/70 text-white backdrop-blur-sm">
                        {work.category}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-1 rounded-md text-xs font-bold bg-accent-500 text-background-950">
                        {work.rating}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-base font-heading font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 mb-2">
                      {work.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                      {work.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line text-sm w-4 h-4 flex items-center justify-center"></i>
                        {work.year}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-film-line text-sm w-4 h-4 flex items-center justify-center"></i>
                        {work.episodes}集
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}